import { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Quote, Plus, Minus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Testimonials = () => {
  const [showAll, setShowAll] = useState(false);
  const [quotes, setQuotes] = useState([]);

  useEffect(() => {
    axios.get(`${API}/testimonials`).then((r) => setQuotes(r.data)).catch(() => {});
  }, []);

  const featured = quotes.filter((q) => q.featured).slice(0, 2);
  const fallback = featured.length ? featured : quotes.slice(0, 2);
  const rest = quotes.filter((q) => !fallback.find((f) => f.id === q.id));

  return (
    <section
      data-testid="testimonials-section"
      className="relative py-20 sm:py-24 bg-black overflow-hidden"
    >
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[300px] bg-brand-purple/15 blur-[140px] rounded-full" />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8">
        <div className="flex items-center gap-3 mb-3">
          <span className="h-px w-8 bg-brand-yellow" />
          <span className="text-xs font-bold uppercase tracking-[0.3em] text-brand-yellow">11 · Testimonials</span>
        </div>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white tracking-wide max-w-3xl">
          Words from the <span className="text-brand-purpleLight">family</span>.
        </h2>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
          {fallback.map((q, i) => (
            <motion.figure
              key={q.id}
              data-testid={`testimonial-${i}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="group relative p-7 rounded-2xl bg-white/[0.025] border border-white/10 hover:border-brand-yellow/40 transition-all"
            >
              <Quote className="text-brand-purpleLight/50 mb-3" size={28} />
              <blockquote className="font-display text-lg sm:text-xl text-white leading-snug tracking-tight">
                "{q.text}"
              </blockquote>
              <figcaption className="mt-5 pt-4 border-t border-white/10">
                <div className="font-bold text-brand-yellow text-xs uppercase tracking-wider">{q.name}</div>
                {q.role && <div className="text-[11px] text-zinc-500 mt-0.5">{q.role}</div>}
              </figcaption>
            </motion.figure>
          ))}
        </div>

        {showAll && rest.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-5"
          >
            {rest.map((q, i) => (
              <figure
                key={q.id}
                data-testid={`testimonial-extra-${i}`}
                className="p-6 rounded-2xl bg-white/[0.02] border border-white/10"
              >
                <blockquote className="text-sm sm:text-base text-zinc-200 leading-snug">"{q.text}"</blockquote>
                <figcaption className="mt-4 pt-3 border-t border-white/10">
                  <div className="font-bold text-brand-yellow text-xs uppercase tracking-wider">{q.name}</div>
                  {q.role && <div className="text-[11px] text-zinc-500 mt-0.5">{q.role}</div>}
                </figcaption>
              </figure>
            ))}
          </motion.div>
        )}

        {rest.length > 0 && (
          <div className="mt-8">
            <button
              data-testid="testimonials-toggle"
              onClick={() => setShowAll((s) => !s)}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/15 text-zinc-300 hover:text-white hover:border-white/40 text-xs font-bold uppercase tracking-[0.2em] transition-all"
            >
              {showAll ? <Minus size={14} /> : <Plus size={14} />}
              {showAll ? "Show less" : "See more stories"}
            </button>
          </div>
        )}
      </div>
    </section>
  );
};
